import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from "react";
import { connect } from "react-redux";

import { IFilterComp, IFilterParams } from "@ag-grid-community/core";
import Radio from "antd/es/radio";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { EntityType, EntityTypeSupportingGroups } from "@mapmycustomers/shared/types/entity";
import type Group from "@mapmycustomers/shared/types/entity/Group";
import useRefState from "@mapmycustomers/shared/util/hook/useRefState";
import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";
import { Button } from "@mapmycustomers/ui";

import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import companyTableAnalytics from "@app/scene/company/companyTableAnalytics";
import peopleTableAnalytics from "@app/scene/people/peopleTableAnalytics";
import { getGroupsForEntity } from "@app/store/groups";
import { RootState } from "@app/store/rootReducer";
import { GroupFilterCondition } from "@app/types/viewModel/agGridModel/AgGridFilterModel";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import styles from "./GroupsFilter.module.scss";

interface Props extends IFilterParams {
  analyticIssuer?: AnalyticsService;
  groups: Group[];
}

const GroupsFilter = forwardRef<Partial<IFilterComp>, Props>(
  ({ analyticIssuer, filterChangedCallback, groups, valueGetter }, ref) => {
    const intl = useIntl();

    const forceRender = useRerenderer();
    const [inAll, setInAll] = useRefState(false, forceRender);
    const [selectedGroupIds, setSelectedGroupIds] = useRefState<Set<Group["id"]>>(
      new Set(),
      forceRender
    );

    useImperativeHandle(
      ref,
      () => {
        return {
          doesFilterPass(params): boolean {
            const groups: Group[] = valueGetter(params.node);
            const groupIds = new Set(groups.map(({ id }) => id));
            // using iterator to avoid converting set into array with Array.prototype.from for
            // performance reasons in cases when doesFilterPass is applied to a large grid
            const valuesIterator = selectedGroupIds.current.values();
            let iterationResult: IteratorResult<Group["id"]> = valuesIterator.next();

            if (inAll) {
              // all selectedGroupIds are present in groupIds
              while (!iterationResult.done && groupIds.has(iterationResult.value)) {
                iterationResult = valuesIterator.next();
              }
              return !!iterationResult.done;
            } else {
              // any selectedGroupId is present in groupIds
              while (!iterationResult.done && !groupIds.has(iterationResult.value)) {
                iterationResult = valuesIterator.next();
              }
              return !iterationResult.done;
            }
          },

          getModel(): GroupFilterCondition | undefined {
            return selectedGroupIds.current.size > 0
              ? {
                  filterType: "groupsFilter",
                  operator: inAll.current ? FilterOperator.IN_ALL : FilterOperator.IN_ANY,
                  value: Array.from(selectedGroupIds.current),
                }
              : undefined;
          },

          isFilterActive(): boolean {
            return selectedGroupIds.current.size > 0;
          },

          setModel(model: GroupFilterCondition | undefined) {
            if (model) {
              setInAll(model.operator === FilterOperator.IN_ALL);
              setSelectedGroupIds(new Set(model.value));
            } else {
              setSelectedGroupIds(new Set());
            }
          },
        };
      },
      [inAll, selectedGroupIds, setInAll, setSelectedGroupIds, valueGetter]
    );

    const groupOptions = useMemo(() => {
      return [...groups]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((group) => ({
          label: group.name,
          value: group.id,
        }));
    }, [groups]);

    const selectValues = useMemo(
      () => Array.from(selectedGroupIds.current),
      // disable next line because we need to listen to changes inside the ref, not the ref itself
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedGroupIds.current]
    );

    const handleGroupSelectionChange = useCallback(
      (checkedValues: Array<Group["id"]>) => {
        setSelectedGroupIds(new Set(checkedValues));
      },
      [setSelectedGroupIds]
    );

    const handleFilterStrategyChange = useCallback(
      (event) => {
        setInAll(event.target.value);
        analyticIssuer?.completed("Apply");
      },
      [analyticIssuer, setInAll]
    );

    const handleApplyClick = useCallback(() => {
      filterChangedCallback();
      analyticIssuer?.completed("Apply");
    }, [analyticIssuer, filterChangedCallback]);

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Radio.Group
            name="groupFilterStrategy"
            onChange={handleFilterStrategyChange}
            size="small"
            value={inAll.current}
          >
            <Radio value={true}>
              {intl.formatMessage({
                id: "grid.groupFilter.strategy.inAll",
                defaultMessage: "Records with all groups",
                description: "Filtering strategy name in GridFilter",
              })}
            </Radio>
            <Radio value={false}>
              {intl.formatMessage({
                id: "grid.groupFilter.strategy.inAny",
                defaultMessage: "Records with any groups",
                description: "Filtering strategy name in GridFilter",
              })}
            </Radio>
          </Radio.Group>
        </div>
        <div className={styles.groupList}>
          <Select<Array<Group["id"]>>
            className={styles.select}
            // more info about this class: https://www.ag-grid.com/javascript-data-grid/component-filter/#custom-filters-containing-a-popup-element
            dropdownClassName="ag-custom-component-popup"
            filterOption={defaultFilterOption}
            mode="multiple"
            onChange={handleGroupSelectionChange}
            options={groupOptions}
            placeholder={intl.formatMessage({
              id: "grid.groupFilter.select.placeholder",
              defaultMessage: "Click or type to select groups",
              description: "Placeholder displayed in a GridFilter's select field",
            })}
            size="small"
            value={selectValues}
          />
        </div>
        <div className={styles.footer}>
          <Button onClick={handleApplyClick} size="small" type="primary">
            {intl.formatMessage({
              id: "grid.groupFilter.apply",
              defaultMessage: "Apply",
              description: "Apply button title in GridFilter",
            })}
          </Button>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (
  state: RootState,
  ownProps: { entityType: EntityTypeSupportingGroups }
) => ({
  groups: getGroupsForEntity(state)(ownProps.entityType),
});

const ConnectedGroupFilter = connect(mapStateToProps, null, null, {
  forwardRef: true,
})(GroupsFilter);

const buildEntitySpecificGroupFilter = (
  entityType: EntityTypeSupportingGroups,
  analyticIssuer?: AnalyticsService
) =>
  forwardRef<Partial<IFilterComp>, IFilterParams>((props, ref) => (
    <ConnectedGroupFilter
      analyticIssuer={analyticIssuer}
      entityType={entityType}
      ref={ref}
      {...props}
    />
  ));

export const CompanyGroupFilter = buildEntitySpecificGroupFilter(
  EntityType.COMPANY,
  companyTableAnalytics.extend(["Side Filter", "Group"])
);
export const PeopleGroupFilter = buildEntitySpecificGroupFilter(
  EntityType.PERSON,
  peopleTableAnalytics.extend(["Side Filter", "Group"])
);
export const DealGroupFilter = buildEntitySpecificGroupFilter(EntityType.DEAL);

export default ConnectedGroupFilter;
